import * as Sentry from '@sentry/browser';
Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,

    // Use proper environment option instead of tag
    environment: import.meta.env.MODE,

    // Use release option for version tracking (enables regression detection)
    release: import.meta.env.VITE_APP_VERSION,

    sendDefaultPii: true,

    integrations: [
        Sentry.browserTracingIntegration(),

        // Only capture console errors in production (reduce dev noise)
        ...(import.meta.env.PROD ? [Sentry.consoleLoggingIntegration({ levels: ['error'] })] : []),

        // Auto-capture failed fetch/XHR requests with request/response info
        Sentry.httpClientIntegration(),

        // Extract all non-native error attributes for richer context
        Sentry.extraErrorDataIntegration(),
    ],

    // Limit breadcrumbs to prevent payload size issues
    maxBreadcrumbs: 50,

    tracesSampleRate: 1.0,
    tracePropagationTargets: (() => {
        try {
            return JSON.parse(import.meta.env.VITE_SENTRY_PROPAGATION_TARGETS);
        } catch {
            return [];
        }
    })(),
});

import './styles/main.css';
import './components/app-shell.js';
import './services/context/auth-context.js';
import './pages/sign-in-page.js';
import './pages/quote-landing-page.js';
import { Router } from './utils/router.js';

export const router = new Router();

// Register routes
// Public routes (no auth required)
router.register(
    '/sign-in',
    () => {
        const app = document.querySelector('app-shell');
        if (app) app.currentPage = 'sign-in';
    },
    { guestOnly: true }
);

// Protected routes (auth required)
router.register(
    '/',
    () => {
        const app = document.querySelector('app-shell');
        if (app) app.currentPage = 'quote-landing';
    },
    { requiresAuth: true }
);

router.register(
    '/quote/:reference',
    (params) => {
        const app = document.querySelector('app-shell');
        if (app) {
            app.currentPage = 'quote-details';
            app.quoteReference = params.reference;
        }
    },
    { requiresAuth: true }
);

// Initialize app
const app = document.createElement('app-shell');
document.getElementById('app').appendChild(app);

// Wait for auth-context-provider to be defined and the first Lit render to
// settle so the router's magic-link branch never fires onAuthStateChanged
// before the context provider has subscribed. 5s timeout falls through to
// router.initialize anyway -- if the app is hung at that point, the router
// not running is the least of our problems.
const _routerReady = Promise.race([
    Promise.all([
        customElements.whenDefined('auth-context-provider'),
        app.updateComplete,
    ]),
    new Promise((resolve) => setTimeout(resolve, 5000)),
]);

_routerReady
    .then(() => router.initialize())
    .catch((error) => {
        Sentry.captureException(error, {
            tags: { feature: 'router-init' },
            level: 'fatal',
        });
    });
